import React from "react";

import QueryStatsTable, {
  QueryStatsTableSortBy,
} from "features/Queries/QueryStatsTable";

import { useQueryStringSort } from "utils/hooks";

import {
  ServerGroupPerDatabaseQueryTableQuery,
  useServerGroupPerDatabaseQueryTableQuery,
} from "./gql/Query.generated";

interface Props {
  serverGroupId: string;
  datname: string;
  startTs: number;
  endTs: number;
  statementTypes: Array<string>;
  searchTerm: null | string;
  compare: boolean;
  merge: boolean;
  outliersOnly: boolean;
}

const FETCH_BATCH_SIZE = 100;

const DEFAULT_SORT = {
  sortBy: "pctOfTotal",
  sortDirection: "DESC",
} as const;

function QueryTable({
  serverGroupId,
  datname,
  startTs,
  endTs,
  statementTypes,
  searchTerm,
  compare,
  merge,
  outliersOnly,
}: Props) {
  const [urlSortOpts, setSortOpts] = useQueryStringSort<QueryStatsTableSortBy>({
    param: "sort",
    ...DEFAULT_SORT,
  });
  const sortOpts = (
    merge
      ? urlSortOpts.sortBy.endsWith("Delta")
      : urlSortOpts.sortBy === "representativeAvgTime"
  )
    ? DEFAULT_SORT
    : urlSortOpts;

  const { data, loading, error, fetchMore } =
    useServerGroupPerDatabaseQueryTableQuery({
      variables: {
        serverGroupId,
        datname,
        startTs,
        endTs,
        statementTypes,
        offset: 0,
        limit: FETCH_BATCH_SIZE,
        filter: searchTerm,
        sortBy: sortOpts.sortBy,
        sortDirection: sortOpts.sortDirection,
        compare,
        merge,
        outliersOnly,
      },
    });

  const fetchMoreData = (loadedCount: number) => {
    return new Promise<boolean>((resolve) => {
      const offset = loadedCount;
      fetchMore({
        variables: { offset, limit: FETCH_BATCH_SIZE },
        updateQuery: (
          prev,
          { fetchMoreResult },
        ): ServerGroupPerDatabaseQueryTableQuery => {
          const fetched = fetchMoreResult.getQueryStatsByDatabaseGroup;
          resolve(fetched.length > 0);
          if (!prev || !prev.getQueryStatsByDatabaseGroup) {
            return fetchMoreResult;
          }
          return {
            ...prev,
            getQueryStatsByDatabaseGroup: [
              ...prev.getQueryStatsByDatabaseGroup,
              ...fetched,
            ],
          };
        },
      });
    });
  };

  return (
    <QueryStatsTable
      queries={data?.getQueryStatsByDatabaseGroup ?? []}
      showServer
      merged={merge}
      loading={loading && !data}
      error={!!error}
      noRowsText={
        searchTerm
          ? "No queries match your search."
          : outliersOnly
          ? "No queries have an outlier database."
          : "No query statistics available."
      }
      fetchMoreData={fetchMoreData}
      sortOpts={sortOpts}
      setSortOpts={setSortOpts}
      searchTerm={searchTerm}
      compare={compare}
    />
  );
}

export default QueryTable;
